import React, { useState, useEffect } from 'react';
import {
    User, Edit2, LogOut, Shield,
    Settings, Heart, Check, ChevronRight, Award
} from 'lucide-react';
import { auth, db, loginWithGoogle, logout } from '../utils/firebase';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { unlockAchievement, getAchievements, getTotalPoints } from '../services/achievementService';


// --- Types ---
interface UserData {
    name: string;
    email: string;
    phone: string;
    createdAt?: any;
}

interface PersonalizationAnswers {
    [key: string]: string[];
}

// --- Constants (Questions) ---
const PERSONALIZATION_QUESTIONS = [
    { id: 'mood', question: 'Tatil ruh hâlin hangileri?', options: ['Romantik kaçamak', 'Macera dolu keşif', 'Aile odaklı rahatlık', 'Lüks & konfor', 'Doğa ile iç içe sakinlik', 'Kültür & tarih peşinde'] },
    { id: 'climate', question: 'En sevdiğin iklim?', options: ['Sıcak güneşli plaj', 'Ilıman bahar esintisi', 'Serin dağ havası', 'Kuru çöl sıcağı', 'Kar & kış sporları', 'Fark etmez, sürpriz severim'] },
    { id: 'scenery', question: 'Hangi manzaralara bayılırsın?', options: ['Deniz & plaj', 'Dağ & orman', 'Göl kenarı', 'Şehir silüeti', 'Tarihi sokaklar & kaleler', 'Çöl & kanyon'] },
    { id: 'accommodation', question: 'Konaklama stilin?', options: ['Butik otel', '5★ resort', 'Glamping / bungalov', 'Şehir apart / Airbnb', 'Hostel & paylaşımlı', 'Temalı/lüks mağara otel'] },
    { id: 'food', question: 'Yemek önceliklerin?', options: ['Deniz ürünleri', 'Sokak lezzetleri', 'Vejetaryen / vegan', 'Fine-dining gurme', 'Tatlı & pastane', 'Yerel ev yemekleri'] },
    { id: 'activities', question: 'Seyahatte hangi aktiviteler?', options: ['Doğa sporları (trek, dalış)', 'Müzeler & sanat', 'Gece hayatı', 'Spa & wellness', 'Alışveriş & moda', 'Fotoğraf / Instagram rotası'] },
    { id: 'budget', question: 'Bütçe modun?', options: ['Backpacker (ekonomik)', 'Orta seviye konfor', 'Lüks, sınır yok', '“Fırsatçı” (kampanya avcısı)', 'Her şey dahil ama uygun', '“Deneyim önemli, fiyat esnek”'] },
    { id: 'company', question: 'Kimlerle geziyorsun?', options: ['Yalnız keşif', 'Romantik partner', 'Aile & çocuklar', 'Arkadaş grubu', 'Evcil hayvanlı', 'İş & bleisure'] },
    { id: 'transport', question: 'Ulaşım konfor tercihin?', options: ['Otobüs – standart', 'Otobüs – VIP 2 + 1', 'Uçak olmazsa olmaz', 'Özel araç / kiralık', 'Tren / ekspres', 'Yavaş seyahat (bisiklet, yürüyüş)'] },
    { id: 'avoid', question: 'Kaçınmak istediklerin?', options: ['Kalabalık turistik bölgeler', 'Gürültülü gece hayatı', 'Aşırı sıcak iklim', 'Yüksek rakım / dağ', 'Deniz ürünlü menü', 'Uzun yolculuk süreleri'] }
];

// --- Sub-Components ---

const ProfileHeader = ({
    user,
    userData,
    stats
}: {
    user: FirebaseUser | null,
    userData: UserData,
    stats: { points: number, unlockedAchievements: number, visitedCities: number }
}) => {
    return (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 border border-slate-100">
            <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Avatar & Info */}
                <div className="flex flex-col items-center md:items-start text-center md:text-left flex-1">
                    <div className="relative mb-4 md:mb-0 md:flex md:items-center md:gap-6">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                            {userData.name ? userData.name.charAt(0).toUpperCase() : <User />}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{userData.name || 'Misafir Gezgin'}</h1>
                            <p className="text-slate-500 font-medium">Hafta sonu kaçamaklarını seven gezgin</p>
                            <div className="flex items-center justify-center md:justify-start gap-2 mt-2 text-xs text-slate-400">
                                <Shield size={14} />
                                <span>{user ? 'Doğrulanmış Hesap' : 'Misafir Hesabı'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="flex gap-4 md:gap-8 w-full md:w-auto justify-center md:justify-end">
                    <div className="text-center p-3 bg-slate-50 rounded-xl min-w-[100px]">
                        <div className="text-2xl font-bold text-blue-600">{stats.points}</div>
                        <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mt-1">Puan</div>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-xl min-w-[100px]">
                        <div className="text-2xl font-bold text-green-600">{stats.unlockedAchievements}</div>
                        <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mt-1">Başarım</div>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-xl min-w-[100px]">
                        <div className="text-2xl font-bold text-purple-600">{stats.visitedCities}</div>
                        <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mt-1">Şehir</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AccountCard = ({
    userData,
    isEditing,
    onEditToggle,
    onSave,
    onCancel,
    onChange,
    saving
}: {
    userData: UserData,
    isEditing: boolean,
    onEditToggle: () => void,
    onSave: () => void,
    onCancel: () => void,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    saving: boolean
}) => {
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden h-fit">
            <div className="p-6 border-b border-slate-50 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Settings size={20} className="text-slate-400" />
                    Hesap Bilgilerin
                </h2>
                {!isEditing && (
                    <button onClick={onEditToggle} className="text-sm text-blue-600 font-medium hover:text-blue-700 flex items-center gap-1">
                        <Edit2 size={14} /> Düzenle
                    </button>
                )}
            </div>

            <div className="p-6 space-y-4">
                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">İsim Soyisim</label>
                    <input
                        type="text"
                        name="name"
                        value={userData.name}
                        onChange={onChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:opacity-75 disabled:cursor-not-allowed"
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">E-posta Adresi</label>
                    <input
                        type="email"
                        name="email"
                        value={userData.email}
                        onChange={onChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:opacity-75 disabled:cursor-not-allowed"
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Telefon</label>
                    <input
                        type="tel"
                        name="phone"
                        value={userData.phone}
                        onChange={onChange}
                        disabled={!isEditing}
                        placeholder="+90 5XX XXX XX XX"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:opacity-75 disabled:cursor-not-allowed"
                    />
                </div>

                {isEditing && (
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onCancel}
                            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                        >
                            Vazgeç
                        </button>
                        <button
                            onClick={onSave}
                            disabled={saving}
                            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-70"
                        >
                            {saving ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>
                )}

                <div className="pt-4 border-t border-slate-50">
                    <button className="text-sm text-slate-500 hover:text-slate-700 font-medium flex items-center gap-2 transition-colors">
                        <Shield size={16} /> Şifreyi Değiştir
                    </button>
                </div>

                <div className="bg-blue-50/50 rounded-xl p-4 mt-4">
                    <p className="text-xs text-blue-600/80 leading-relaxed">
                        Bu bilgiler, seni daha iyi tanımamız ve gelecekte seyahat önerilerini kişiselleştirmemiz için kullanılır.
                    </p>
                </div>
            </div>
        </div>
    );
};

const TravelDNACard = ({
    answers,
    onToggleOption,
    onSave,
    saving
}: {
    answers: PersonalizationAnswers,
    onToggleOption: (qId: string, opt: string) => void,
    onSave: () => void,
    saving: boolean
}) => {
    const totalQuestions = PERSONALIZATION_QUESTIONS.length;
    const answeredCount = Object.values(answers).filter(a => a && a.length > 0).length;
    const progress = (answeredCount / totalQuestions) * 100;

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-50">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Heart size={20} className="text-red-400" />
                            Tatil DNA'n
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Birkaç soruya cevap ver, sana özel rota ve etkinlikleri filtreleyelim.
                        </p>
                    </div>
                    <div className="text-right">
                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
                            {answeredCount}/{totalQuestions}
                        </span>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-blue-400 to-cyan-400 transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            <div className="p-6 space-y-8 max-h-[600px] overflow-y-auto custom-scrollbar">
                {PERSONALIZATION_QUESTIONS.map((q) => (
                    <div key={q.id}>
                        <h3 className="text-sm font-bold text-gray-800 mb-3">{q.question}</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {q.options.map((opt) => {
                                const isSelected = answers[q.id]?.includes(opt);
                                return (
                                    <button
                                        key={opt}
                                        onClick={() => onToggleOption(q.id, opt)}
                                        className={`
                      relative px-3 py-2 text-xs sm:text-sm font-medium rounded-xl border text-left transition-all duration-200
                      ${isSelected
                                                ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                                : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-slate-50'}
                    `}
                                    >
                                        {opt}
                                        {isSelected && (
                                            <div className="absolute top-1 right-1 text-blue-500">
                                                <Check size={12} strokeWidth={3} />
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                <p className="text-xs text-slate-400 font-medium">
                    Dilediğin zaman güncelleyebilirsin.
                </p>
                <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-6 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-800 transition-colors shadow-lg disabled:opacity-70"
                >
                    {saving ? 'Kaydediliyor...' : 'Tercihlerimi Kaydet'}
                </button>
            </div>
        </div>
    );
};

const AchievementsTeaser = () => {
    return (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white flex items-center justify-between mt-8">
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Award size={24} className="text-white" />
                </div>
                <div>
                    <h3 className="font-bold text-lg">Başarımlarını Keşfet</h3>
                    <p className="text-indigo-100 text-sm">Rozetlerini topla, gezgin seviyeni yükselt!</p>
                </div>
            </div>
            <a
                href="/achievements"
                className="px-5 py-2 bg-white text-indigo-600 font-bold rounded-xl text-sm hover:bg-indigo-50 transition-colors flex items-center gap-2"
            >
                Görüntüle <ChevronRight size={16} />
            </a>
        </div>
    );
};

// --- Main Page Component ---

const ProfilePage = () => {
    const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
    const [userData, setUserData] = useState<UserData>({ name: '', email: '', phone: '' });
    const [originalUserData, setOriginalUserData] = useState<UserData>({ name: '', email: '', phone: '' });
    const [answers, setAnswers] = useState<PersonalizationAnswers>({});
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState({ points: 0, unlockedAchievements: 0, visitedCities: 0 });

    // Load Data
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                // Fetch Profile
                const userRef = doc(db, 'users', user.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const data = userSnap.data();
                    const hydrated = {
                        name: data.name || user.displayName || '',
                        email: data.email || user.email || '',
                        phone: data.phone || ''
                    };
                    setUserData(hydrated);
                    setOriginalUserData(hydrated);

                    // Fetch Stats
                    const achievements = await getAchievements();
                    const points = await getTotalPoints();
                    const visitedCities = data.stats?.visitedCities?.length || 0;
                    const unlockedCount = achievements.filter(a => a.unlocked).length;

                    setStats({
                        points,
                        unlockedAchievements: unlockedCount,
                        visitedCities
                    });
                }

                // Fetch Personalization
                const prefRef = doc(db, 'userPreferences', user.uid);
                const prefSnap = await getDoc(prefRef);
                if (prefSnap.exists()) {
                    setAnswers(prefSnap.data() as PersonalizationAnswers);
                }
            } else {
                // Guest / Local Storage Fallback
                const storedUser = localStorage.getItem('seyHotUserData');
                if (storedUser) {
                    const parsed = JSON.parse(storedUser);
                    setUserData(parsed);
                    setOriginalUserData(parsed);
                }
            }
        });
        return () => unsub();
    }, []);

    // Handlers
    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUserData(prev => ({ ...prev, [name]: value }));
    };

    const handleProfileSave = async () => {
        setSaving(true);
        try {
            if (currentUser) {
                const userRef = doc(db, 'users', currentUser.uid);
                await setDoc(userRef, {
                    name: userData.name,
                    email: userData.email,
                    phone: userData.phone, // Note: Storing phone in Firestore as per new design implication
                    updatedAt: serverTimestamp()
                }, { merge: true });
            }
            localStorage.setItem('seyHotUserData', JSON.stringify(userData));
            setOriginalUserData(userData);
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving profile:', error);
            alert('Profil kaydedilirken bir hata oluştu.');
        } finally {
            setSaving(false);
        }
    };

    const handleOptionToggle = (qId: string, opt: string) => {
        setAnswers(prev => {
            const current = prev[qId] || [];
            const updated = current.includes(opt)
                ? current.filter(i => i !== opt)
                : [...current, opt];
            return { ...prev, [qId]: updated };
        });
    };

    const handlePersonalizationSave = async () => {
        if (!currentUser) {
            alert("Lütfen tercihlerinizi kaydetmek için giriş yapın.");
            return;
        }
        setSaving(true);
        try {
            const prefRef = doc(db, 'userPreferences', currentUser.uid);
            await setDoc(prefRef, answers, { merge: true });

            // Check Achievement
            const unlocked = await unlockAchievement('holiday-dna');
            if (unlocked) {
                alert("Tercihleriniz kaydedildi ve 'Tatil DNA'nı Çözdün' başarımı açıldı! 🧬");
            } else {
                alert("Tercihleriniz başarıyla güncellendi!");
            }
        } catch (error) {
            console.error("Error saving preferences:", error);
            alert("Tercihler kaydedilemedi.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f5fefd] py-12 px-4 sm:px-6 lg:px-8 font-sans">
            <div className="max-w-6xl mx-auto">

                {/* Header Section */}
                <ProfileHeader user={currentUser} userData={userData} stats={stats} />

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                    {/* Left Column: Account Info */}
                    <div className="lg:col-span-4 space-y-6">
                        <AccountCard
                            userData={userData}
                            isEditing={isEditing}
                            onEditToggle={() => setIsEditing(true)}
                            onCancel={() => {
                                setUserData(originalUserData);
                                setIsEditing(false);
                            }}
                            onSave={handleProfileSave}
                            onChange={handleProfileChange}
                            saving={saving}
                        />

                        {/* Auth Actions */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                            <h3 className="text-sm font-bold text-gray-900 mb-4">Oturum İşlemleri</h3>
                            {!currentUser ? (
                                <button
                                    onClick={loginWithGoogle}
                                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                                >
                                    <User size={18} /> Google ile Giriş Yap
                                </button>
                            ) : (
                                <button
                                    onClick={logout}
                                    className="w-full py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                                >
                                    <LogOut size={18} /> Çıkış Yap
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Travel DNA */}
                    <div className="lg:col-span-8">
                        <TravelDNACard
                            answers={answers}
                            onToggleOption={handleOptionToggle}
                            onSave={handlePersonalizationSave}
                            saving={saving}
                        />

                        <AchievementsTeaser />
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
